/**
 * SSOT do dominio raiz da plataforma e da regex de slug de tenant.
 *
 * Resolve em runtime via env NEXT_PUBLIC_PLATFORM_ROOT_DOMAIN +
 * NEXT_PUBLIC_PLATFORM_SUBDOMAIN. Existe como excecao de bootstrap:
 * edge runtime do middleware Next.js e o client bundle nao podem
 * consultar Prisma para ler PlatformConfig do banco.
 *
 * SSOT real: PlatformConfig.rootDomain + PlatformConfig.platformSubdomain
 * no banco. O workflow `deploy-platform.yml` lê o banco em build-time
 * (resolve_runtime_config) e injeta como --build-arg. O operador
 * atualiza o banco em cada cutover de dominio.
 *
 * Estados suportados (ADR-019):
 * - Legacy (uso 7cors.com.br): PLATFORM_DOMAIN=7cors.com.br,
 *   PLATFORM_SUBDOMAIN=barber -> PLATFORM_HOST=barber.7cors.com.br
 * - Cutover (uso 7barber.app): PLATFORM_DOMAIN=7barber.app,
 *   PLATFORM_SUBDOMAIN='' -> PLATFORM_HOST=7barber.app
 *
 * Centralizado aqui para eliminar 3 duplicacoes que existiam:
 *   - middleware.ts
 *   - next.config.js
 *   - lib/branding.ts
 *
 * next.config.js (CommonJS) le process.env direto - nao importa este
 * modulo, mas deve usar as mesmas env vars.
 */

export const PLATFORM_DOMAIN =
  process.env.NEXT_PUBLIC_PLATFORM_ROOT_DOMAIN ?? '7barber.app';

/**
 * Dominio-raiz SECUNDARIO - so para DEV local. Permite alcancar os tenants por
 * um segundo host na MESMA instancia, ao mesmo tempo: ex. `<slug>.localhost` no
 * PC e `<slug>.<IP>.nip.io` no celular. Vazio em producao (so o `.env.local`
 * define). Nunca e SSOT - existe apenas para teste local multi-dispositivo.
 */
export const PLATFORM_DOMAIN_ALT = (
  process.env.NEXT_PUBLIC_PLATFORM_ROOT_DOMAIN_ALT ?? ''
).trim();

/**
 * Subdominio opcional da plataforma raiz. Vazio quando a plataforma
 * vive no apex (default pos-ADR-019). Ainda usado pelo legacy
 * `barber.7cors.com.br` se voltarmos a configurar.
 */
export const PLATFORM_SUBDOMAIN = (
  process.env.NEXT_PUBLIC_PLATFORM_SUBDOMAIN ?? ''
).trim();

/** Host canonico da plataforma raiz (super-admin/login/register/pricing). */
export const PLATFORM_HOST = PLATFORM_SUBDOMAIN
  ? `${PLATFORM_SUBDOMAIN}.${PLATFORM_DOMAIN}`
  : PLATFORM_DOMAIN;

/**
 * Regex oficial de validacao de slug de tenant. RFC subdomain + lowercase
 * only. Aceita ate 63 chars, deve comecar/terminar com alfanumerico.
 *
 * Centralizado aqui para evitar divergencia entre:
 *   - lib/validators.ts (zod schema do form)
 *   - middleware.ts (TENANT_HOST_RE extrai slug do hostname)
 *   - components/auth/SlugPreview.tsx (preview live)
 *   - api/src/modules/platform/check-slug.controller.ts (backend valida)
 */
export const TENANT_SLUG_REGEX =
  /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

/**
 * Regex de normalizacao - remove tudo que nao for [a-z0-9-]. Usado em
 * preview de slug ao usuario digitar o nome da barbearia.
 */
export const TENANT_SLUG_NORMALIZER_REGEX = /[^a-z0-9-]/g;

/**
 * Extrai o slug do tenant a partir do hostname atual do navegador.
 * Retorna null quando esta no host canonico da plataforma (login global,
 * super-admin) ou em localhost de dev. Usado pelo firePlatformToast e
 * outros helpers que precisam aplicar overrides per-tenant.
 *
 * Convencao: `<slug>.<PLATFORM_DOMAIN>` => slug. `PLATFORM_HOST`
 * (host raiz, com ou sem subdomain) => null. Qualquer outro padrao
 * (localhost, IP, etc) => null.
 *
 * Funciona apenas no client (depende de window.location).
 */
export function getCurrentTenantSlug(): string | null {
  if (typeof window === 'undefined') return null;
  const host = window.location.hostname.toLowerCase();
  // host raiz da plataforma nunca eh tenant
  if (host === PLATFORM_HOST) return null;
  // Tenta o dominio raiz e, em DEV, tambem o secundario (PLATFORM_DOMAIN_ALT).
  for (const domain of [PLATFORM_DOMAIN, PLATFORM_DOMAIN_ALT]) {
    if (!domain || host === domain) continue;
    const suffix = '.' + domain;
    if (!host.endsWith(suffix)) continue;
    const candidate = host.slice(0, -suffix.length);
    // PLATFORM_SUBDOMAIN ('barber' no legacy, vazio pos-cutover) nao e tenant.
    if (PLATFORM_SUBDOMAIN && candidate === PLATFORM_SUBDOMAIN) continue;
    if (!TENANT_SLUG_REGEX.test(candidate)) continue;
    return candidate;
  }
  return null;
}
